"use client";

import { useLingui } from "@lingui/react/macro";
import Image from "next/image";
import { useEffect, useReducer, useState, useSyncExternalStore, type CSSProperties } from "react";

import { HERO_PHOTOS } from "./content";
import { prefersReducedMotion } from "./motion";
import { useSectionInView } from "./section";

/**
 * The hero's photographs (landing_hero_photos_v1 "fan" arm): Nordic workplaces,
 * from the workshop to the corner office, on an ordinary day, with the agent in
 * its squid hat quietly doing the paperwork behind them. Generated images of
 * invented people: never show these as customers.
 *
 * Two layers. Below is the fan itself, a ring of cards that slides round by one
 * place per photo. Above it each photo has a second copy on exactly the same path,
 * visible only while that photo is in the centre, so a card arriving fades in on
 * top as the card leaving fades out to its copy below. Both keep moving as they
 * dissolve. A single layer cannot do this: the arriving card has to cross over the
 * one it replaces, and visibly pops on top when its z-index changes. Here the copy
 * changes layer while it is invisible. A phone gets only the upper layer, with no
 * fan: the photos dissolve into each other in place.
 *
 * Only the photos the rotation has reached are mounted, so a visitor who leaves
 * early downloads two on a phone and six on desktop. Both layers ask for the same
 * `sizes`, so a photo in both is one download. A photo appears only once it has
 * loaded, so the rotation waits out a slow connection instead of fading to
 * nothing. Under reduced motion the first photo stays.
 *
 * The rotation pauses while the pointer is over the fan or the figure has focus
 * (WCAG 2.2.2), which is why a figure with nothing to activate is a tab stop.
 *
 * One label on the figure rather than alt text per photo: the same sentence is
 * true of all of them, and a screen reader should not hear them change.
 */
const HOLD_MS = 6000;

const FADE_MS = 1200;

const PHOTO_SIZES = "(min-width: 1024px) 58vw, 100vw";

const FAN_MEDIA_QUERY = "(min-width: 1024px)";

/** Cards in the fan on each side of the current one. */
const FAN_REACH = 2;

/** Above every card of the ring, and below the photo that is arriving. */
const TOP_Z_INDEX = FAN_REACH + 3;

const FAN_POSES = [
  { x: 0, y: 0, rotate: 0, scale: 0.7 },
  { x: 16, y: 3, rotate: 6, scale: 0.6 },
  { x: 27, y: 9, rotate: 12, scale: 0.5 },
  { x: 32, y: 14, rotate: 16, scale: 0.44 },
] as const;

/** On a phone a photo fades in over the previous one, which stays opaque until it is fully covered, so the two never let the background through. */
const PHONE_SHOWING: CSSProperties = { opacity: 1, zIndex: 1 };
const PHONE_WAITING: CSSProperties = { opacity: 0, transitionDelay: `${FADE_MS}ms` };

interface Rotation {
  readonly active: number;
  /** How many photos from the start of the list are in the DOM. */
  readonly mounted: number;
  readonly loaded: ReadonlySet<number>;
}

type RotationEvent = { readonly type: "loaded"; readonly index: number } | { readonly type: "tick" };

export const nextRotation = (state: Rotation, event: RotationEvent, total: number): Rotation => {
  if (event.type === "loaded") return { ...state, loaded: new Set(state.loaded).add(event.index) };
  const next = (state.active + 1) % total;
  if (!state.loaded.has(next)) return state;
  return { ...state, active: next, mounted: Math.min(total, Math.max(state.mounted, next + 2)) };
};

const subscribeToFanWidth = (onChange: () => void): (() => void) => {
  const query = window.matchMedia(FAN_MEDIA_QUERY);
  query.addEventListener("change", onChange);
  return () => {
    query.removeEventListener("change", onChange);
  };
};

/** The photo's place relative to the current one on the ring: 0 is in the centre, negative has been shown. */
export const fanOffset = (index: number, active: number, total: number): number => {
  const half = Math.floor(total / 2);
  return ((index - active + total + half) % total) - half;
};

/**
 * Every card past the reach waits at the same hidden pose on its own side, so
 * the one wrapping from the far left to the far right crosses while invisible.
 */
export const fanCardStyle = (offset: number, visible: boolean): CSSProperties => {
  const pose = FAN_POSES[Math.min(Math.abs(offset), FAN_REACH + 1)] ?? FAN_POSES[0];
  const side = Math.sign(offset);
  return {
    transform: `translate(${side * pose.x}%, ${pose.y}%) rotate(${side * pose.rotate}deg) scale(${pose.scale})`,
    opacity: visible && Math.abs(offset) <= FAN_REACH ? 1 : 0,
    zIndex: FAN_REACH + 2 - Math.abs(offset),
  };
};

/** A photo's copy in the upper layer of the fan: on its card's path, and showing only in the centre. */
export const fanTopStyle = (offset: number): CSSProperties => ({
  ...fanCardStyle(offset, true),
  opacity: offset === 0 ? 1 : 0,
  zIndex: offset === 0 ? TOP_Z_INDEX + 1 : TOP_Z_INDEX,
});

const CARD = "absolute inset-0 overflow-hidden rounded-[22px] shadow-[0_30px_60px_-24px_rgba(10,22,40,0.55)] transition-[opacity,transform] ease-in-out motion-reduce:transition-none";

export function HeroPhotos() {
  const { t } = useLingui();
  const inView = useSectionInView();
  const total = HERO_PHOTOS.length;
  const [{ active, mounted, loaded }, dispatch] = useReducer(
    (state: Rotation, event: RotationEvent) => nextRotation(state, event, total),
    { active: 0, mounted: 2, loaded: new Set<number>() },
  );
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const paused = hovered || focused;
  const fanned = useSyncExternalStore(
    subscribeToFanWidth,
    () => window.matchMedia(FAN_MEDIA_QUERY).matches,
    () => false,
  );

  useEffect(() => {
    if (!inView || paused || prefersReducedMotion()) return;
    const timer = setInterval(() => {
      dispatch({ type: "tick" });
    }, HOLD_MS);
    return () => {
      clearInterval(timer);
    };
  }, [inView, paused]);

  // The ring also needs the cards behind the current one, and one more ahead
  // than it shows, so that a card never arrives before its photo has.
  const inRing = (index: number): boolean => index < mounted + FAN_REACH || index >= total - FAN_REACH;

  const photo = (name: string, index: number, priority: boolean) => (
    <Image
      src={`/landing/smb/hero/${name}.webp`}
      alt=""
      fill
      priority={priority}
      sizes={PHOTO_SIZES}
      onLoad={() => {
        dispatch({ type: "loaded", index });
      }}
      className="object-cover"
    />
  );

  return (
    <figure
      aria-label={t`People at work, each with a colleague in a plush squid hat quietly handling the paperwork in the background.`}
      tabIndex={0}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      onFocus={() => {
        setFocused(true);
      }}
      onBlur={() => {
        setFocused(false);
      }}
      className="relative m-0 aspect-[4/3] w-full rounded-[22px] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-signal dark:focus-visible:outline-signal-text"
    >
      {fanned &&
        HERO_PHOTOS.map((name, index) =>
          inRing(index) ? (
            <div
              key={name}
              style={{ ...fanCardStyle(fanOffset(index, active, total), loaded.has(index)), transitionDuration: `${FADE_MS}ms` }}
              className={CARD}
            >
              {photo(name, index, false)}
            </div>
          ) : null,
        )}
      {HERO_PHOTOS.slice(0, mounted).map((name, index) => (
        <div
          key={name}
          style={{
            ...(fanned ? fanTopStyle(fanOffset(index, active, total)) : index === active ? PHONE_SHOWING : PHONE_WAITING),
            transitionDuration: `${FADE_MS}ms`,
          }}
          className={CARD}
        >
          {photo(name, index, index === 0)}
        </div>
      ))}
    </figure>
  );
}
